import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faCheckCircle as farCheckCircle,
  faEnvelope as farEnvelope,
  faHandshake as farHandshake,
  faHdd as farHdd,
  faTimesCircle as farTimesCircle,
} from '@fortawesome/free-regular-svg-icons';
import {
  faAngleDown,
  faAngleUp,
  faArrowAltCircleDown,
  faArrowAltCircleLeft,
  faArrowCircleLeft,
  faArrowLeft,
  faBan,
  faBell,
  faBoxOpen,
  faBriefcase,
  faBullhorn,
  faCalendarAlt,
  faCaretDown,
  faCaretUp,
  faChartArea,
  faChartLine,
  faChartPie,
  faCheck,
  faCheckCircle,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircle,
  faCircleNotch,
  faClipboardList,
  faCog,
  faCogs,
  faCopy,
  faCreditCard,
  faDollarSign,
  faEdit,
  faEquals,
  faExclamationCircle,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
  faFighterJet,
  faFileAlt,
  faFileContract,
  faFileExcel,
  faFileInvoiceDollar,
  faFileUpload,
  faFilter,
  faGripHorizontal,
  faHandshake,
  faHistory,
  faHome,
  faInfo,
  faInfoCircle,
  faKey,
  faLayerGroup,
  faLink,
  faList,
  faListUl,
  faMinusCircle,
  faPause,
  faPlay,
  faPlus,
  faPlusCircle,
  faQuestionCircle,
  faRedoAlt,
  faRobot,
  faSave,
  faSearch,
  faSearchMinus,
  faSearchPlus,
  faShieldAlt,
  faSignOutAlt,
  faSlidersH,
  faSortAmountDown,
  faSortAmountUp,
  faSpinner,
  faSyncAlt,
  faTable,
  faTachometerAlt,
  faTag,
  faTasks,
  faTimes,
  faTimesCircle,
  faTrash,
  faUnlockAlt,
  faUpload,
  faUser,
  faUserCheck,
  faUserCircle,
  faUserCog,
  faUserMd,
  faUsers,
  faUsersCog,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NZ_ICONS } from 'ng-zorro-antd/icon';
import { LottieModule } from 'ngx-lottie';

import { AppZorroModule } from '../app-zorro.module';
import { BlockedListsComponent } from '../features/placements/edit-placement/blocked-lists/blocked-lists.component';
import { PlacementEditComponent } from '../features/placements/edit-placement/edit-placement.component';
import {
  PlacementCommissionsComponent,
} from '../features/placements/edit-placement/placement-commissions/placement-commissions.component';
import { PlacementFloorsComponent } from '../features/placements/edit-placement/placement-floors/placement-floors.component';
import { PlacementsComponent } from '../features/placements/placements.component';
import { Common } from './_common/common.helper';
import { FormHelper } from './_common/form.helper';
import { SYSTEM_ZORRO_ICONS } from './_common/icons';
import { PipeManager } from './_common/pipeManager.helper';
import { AlertsManagementComponent } from './_components/alerts-management/alerts-management.component';
import {
  AddTargetToAlertComponent,
} from './_components/alerts-management/edit-alert/add-target-to-alert/add-target-to-alert.component';
import { EditAlertComponent } from './_components/alerts-management/edit-alert/edit-alert.component';
import { AppChartComponent } from './_components/app-chart/app-chart.component';
import { AppDropdownButtonComponent } from './_components/app-dropdown-button/app-dropdown-button.component';
import { AppFiltersComponent } from './_components/app-filters/app-filters.component';
import { AppInputComponent } from './_components/app-input/app-input.component';
import { AppModalComponent } from './_components/app-modal/app-modal.component';
import { AppTableComponent } from './_components/app-table/app-table.component';
import { PublisherStatusPillComponent } from './_components/publisher-status-pill/publisher-status-pill.component';
import {
  AudienceTargetingSegmentComponent,
} from './_components/audience-targeting-segment/audience-targeting-segment.component';
import { AudienceTargetingComponent } from './_components/audience-targeting/audience-targeting.component';
import { AuditComponent } from './_components/audit/audit.component';
import { BillPaymentInfoComponent } from './_components/billing-history/bill-payment-info/bill-payment-info.component';
import { BillingHistoryComponent } from './_components/billing-history/billing-history.component';
import { BlockedAdvertisersComponent } from './_components/blocked-advertisers/blocked-advertisers.component';
import { CopyToClipboardButtonComponent } from './_components/copy-to-clipboard-button/copy-to-clipboard-button.component';
import {
  ClusivityListMultiTextInputComponent,
} from './_components/clusivity-list/clusivity-list-multi-text-input/clusivity-list-multi-text-input.component';
import {
  ClusivityListSelectOptionGroupsComponent,
} from './_components/clusivity-list/clusivity-list-select-option-groups/clusivity-list-select-option-groups.component';
import {
  ClusivityListSelectOptionsTreeComponent,
} from './_components/clusivity-list/clusivity-list-select-options-tree/clusivity-list-select-options-tree.component';
import {
  ClusivityListSelectOptionsComponent,
} from './_components/clusivity-list/clusivity-list-select-options/clusivity-list-select-options.component';
import { ClusivityListComponent } from './_components/clusivity-list/clusivity-list.component';
import {
  CommissionFixedCpmWarningTextComponent,
} from './_components/commission-fixed-cpm-warning-text/commission-fixed-cpm-warning-text.component';
import { ContentControlsComponent } from './_components/content-controls/content-controls.component';
import { CustomizeColumnsComponent } from './_components/app-customize-columns/app-customize-columns.component';
import { DayPartingAccordionComponent } from './_components/day-parting-accordion/day-parting-accordion.component';
import { DayPartingComponent } from './_components/day-parting/day-parting.component';
import { DealExternalSyncComponent } from './_components/deal-external-sync/deal-external-sync.component';
import { DealPartnerInfoComponent } from './_components/deal-partner-info/deal-partner-info.component';
import { DomainBundlesComponent } from './_components/domain-bundles/domain-bundles.component';
import { ExchangeDealsDropdownComponent } from './_components/exchange-deals-dropdown/exchange-deals-dropdown.component';
import { GeoTargetingComponent } from './_components/geo-targeting/geo-targeting.component';
import { SharedGlobalSearchComponent } from './_components/global-search/global-search.component';
import { SharedKpiWidgetComponent } from './_components/kpi-widget/kpi-widget.component';
import { PasswordValidatorComponent } from './_components/password-validator/password-validator.component';
import { SharedPaymentInfoComponent } from './_components/payment-info/payment-info.component';
import { PlacementsRequiredComponent } from './_components/placements-required/placements-required.component';
import { PublisherDealsDropdownComponent } from './_components/publisher-deals-dropdown/publisher-deals-dropdown.component';
import { PhoneInputComponent } from './_components/phone-input/phone-input.component';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { PublisherSellersComponent } from './_components/publisher-sellers/publisher-sellers.component';
import { SignupInfoComponent } from './_components/signup-info/signup-info.component';
import { SvgAngular } from './_components/svg/angular.component';
import { AppIcon } from './_components/svg/app-icon.component';
import { SvgUnrulyLogo } from './_components/svg/unruly-logo.component';
import { SharedTaxInfoComponent } from './_components/tax-info/tax-info.component';
import { UniversalMediaDomainsComponent } from './_components/universal-media-domains/universal-media-domains.component';
import {
  UniversalMediaListContainerComponent,
} from './_components/universal-media-list-container/universal-media-list-container.component';
import { PadAgreementDropdownComponent } from './_components/pad-agreement-dropdown/pad-agreement-dropdown.component';
import { UploadContractComponent } from './_components/upload-contract/upload-contract.component';
import { UploadCsvComponent } from './_components/upload-csv/upload-csv.component';
import { UploadFileComponent } from './_components/upload-file/upload-file.component';
import { UploadLogoComponent } from './_components/upload-logo/upload-logo.component';
import { UserFormPasswordComponent } from './_components/user-form-password/user-form-password.component';
import { UserManagementUpdateComponent } from './_components/user-management-update/user-management-update.component';
import { UserAccessComponent } from './_components/user-access/user-access.component';
import { UserManagementComponent } from './_components/user-management/user-management/user-management.component';
import { WarningBoxComponent } from './_components/warning-box/warning-box.component';
import { ExchangeAuditComponent } from './_components/exchange-audit/exchange-audit.component';
import { ExchangeAuditDetailsComponent } from './_components/exchange-audit/exchange-audit-details/exchange-audit-details.component';
import { ClickOutsideDirective } from './_directives/click-outside.directive';
import { CustomComponentHostDirective } from './_directives/custom-component-host.directive';
import { EllipsisAndTooltipDirective } from './_directives/ellipsis-and-tooltip.directive';
import { FeatureToggleDirective } from './_directives/feature-toggle.directive';
import { FixedTableHeaderDirective } from './_directives/fixed-table-header.directive';
import { FocusInvalidDirective } from './_directives/focus-invalid.directive';
import { GtmIdImplementDirective } from './_directives/gtm-id-implement.directive';
import { NzDynamicMaxTagCountDirective } from './_directives/nz-dynamic-max-tag-count.directive';
import { OnScrollToElementDirective } from './_directives/on-scroll-to-element.directive';
import { ShowIfTextLeakedDirective } from './_directives/show-if-text-leaked.directive';
import { ErrorInterceptor } from './_interceptors/error.interceptor';
import { FilenameInterceptor } from './_interceptors/filename.interceptor';
import { JwtInterceptor } from './_interceptors/jwt.interceptor';
import { VersionInterceptor } from './_interceptors/version.interceptor';
import { EnumsPipe } from './_pipes/enums.pipe';
import { ExcludeSelectOptionPipe } from './_pipes/exclude-select-option.pipe';
import { FormatTextPipe } from './_pipes/format-text.pipe';
import { IsLoadingPipe } from './_pipes/is-loading.pipe';
import { CellTagPipe } from './_pipes/cell-tag.pipe';
import { LogPipe } from './_pipes/log.pipe';
import { MapFromKeyValuePipe } from './_pipes/mapFromKeyValue.pipe';
import { SanitizerPipe } from './_pipes/sanitizer.pipe';
import { SimplePercentPipe } from './_pipes/simple_percent.pipe';
import { SortByPipe } from './_pipes/sort-by.pipe';
import { StripHtmlPipe } from './_pipes/stripHtml.pipe';
import { TruncatePipe } from './_pipes/truncate.pipe';
import { WidgetFormatPipe } from './_pipes/widgetFormat.pipe';
import { ForbiddenComponent } from './error/forbidden/forbidden.component';
import { MaintenanceComponent } from './error/maintenance/maintenance.component';
import { NotFoundComponent } from './error/notfound/notfound.component';
import { NumericOnlyDirective } from './_directives/numeric-only.directive';
import { AppLoadingComponent } from './_components/app-loading';
import { AngularHttpClientAdapter } from './_services/angular-http-client';
import { AngularServiceConfigProvider } from './_services/angular-service-config';
import { FusionApiHttpClient, RxDealsHttpClient } from './_services/server-clients';
import { TrafficSourcesApiService } from '../features/supply/traffic-sources/traffic-sources.api-service';
import { UniversalMediaListsApiService } from '../features/universal-media-lists/universal-media-lists.api-service';
import { ExchangeBrandsApiService } from '../features/exchange-brands/exchange-brands.api-service';
import { ExchangeDealFormApiService } from '../features/exchange-deals/exchange-deal-form/_services/exchange-deal-form.api-service';
import { SupplyPadAgreementApiService } from '../features/pad-agreements/supply-pad-agreement.api-service';
import { PublishersApiService } from '../features/supply/publishers/_services/publishers.api-service';
import { ExchangePartnersApiService } from '../features/exchange-partners/exchange-partners.api-service';
import {
  PublisherTrafficSourceApiService
} from '../features/supply/publishers/publisher-configurations/_services/publisher-traffic-source.api-service';
import { InventoryPackagesApiService } from '../features/inventory-packages/inventory-packages.api-service';
import { DealsApiService } from '../features/deals/deals.api-service';
import { HttpClient } from '@angular/common/http';

function trafficSourcesApiServiceFactory(fusionApiHttpClient: FusionApiHttpClient) {
  return new TrafficSourcesApiService(fusionApiHttpClient);
}

function supplyPadAgreementApiServiceFactory(fusionApiHttpClient: FusionApiHttpClient) {
  return new SupplyPadAgreementApiService(fusionApiHttpClient);
}

function publishersApiServiceFactory(fusionApiHttpClient: FusionApiHttpClient) {
  return new PublishersApiService(fusionApiHttpClient);
}

function exchangePartnersApiServiceFactory(fusionApiHttpClient: FusionApiHttpClient) {
  return new ExchangePartnersApiService(fusionApiHttpClient);
}

function publisherTrafficSourceApiServiceFactory(fusionApiHttpClient: FusionApiHttpClient) {
  return new PublisherTrafficSourceApiService(fusionApiHttpClient);
}

function inventoryPackagesApiServiceFactory(fusionApiHttpClient: FusionApiHttpClient) {
  return new InventoryPackagesApiService(fusionApiHttpClient);
}

function dealsApiServiceFactory(fusionApiHttpClient: FusionApiHttpClient) {
  return new DealsApiService(fusionApiHttpClient);
}

const playerFactory = () => import('lottie-web');

function fusionApiHttpClientFactory(http: HttpClient, configService: AngularServiceConfigProvider) {
  const httpClient = new AngularHttpClientAdapter(http);
  const serviceConfig = configService.getConfig();
  return new FusionApiHttpClient(httpClient, serviceConfig);
}

function rxDealsHttpClientFactory(http: HttpClient, configService: AngularServiceConfigProvider) {
  const httpClient = new AngularHttpClientAdapter(http);
  const serviceConfig = configService.getConfig();
  return new RxDealsHttpClient(httpClient, serviceConfig);
}

function universalMediaListsApiServiceFactory(rxDealsHttpClient: RxDealsHttpClient) {
  return new UniversalMediaListsApiService(rxDealsHttpClient);
}

function exchangeBrandsApiServiceFactory(rxDealsHttpClient: RxDealsHttpClient) {
  return new ExchangeBrandsApiService(rxDealsHttpClient);
}

function exchangeDealFormApiServiceFactory(rxDealsHttpClient: RxDealsHttpClient) {
  return new ExchangeDealFormApiService(rxDealsHttpClient);
}

@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgxChartsModule,
    FontAwesomeModule,
    RouterModule,
    ScrollingModule,
    AppZorroModule,
    LottieModule.forRoot({ player: playerFactory }),
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzGridModule
  ],
  exports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AppZorroModule,
    NgxChartsModule,
    FontAwesomeModule,
    ScrollingModule,
    CKEditorModule,
    TruncatePipe,
    MapFromKeyValuePipe,
    WidgetFormatPipe,
    SimplePercentPipe,
    StripHtmlPipe,
    AppTableComponent,
    PublisherStatusPillComponent,
    AppFiltersComponent,
    LogPipe,
    SortByPipe,
    IsLoadingPipe,
    CellTagPipe,
    // Assets
    AppIcon,
    SvgUnrulyLogo,
    SvgAngular,
    BillingHistoryComponent,
    FocusInvalidDirective,
    SharedTaxInfoComponent,
    SharedPaymentInfoComponent,
    SharedKpiWidgetComponent,
    AppChartComponent,
    // AppBarChartComponent,
    // MultiSelectionDropdownComponent,
    ClickOutsideDirective,
    PlacementsRequiredComponent,
    SharedGlobalSearchComponent,
    SignupInfoComponent,
    UserManagementUpdateComponent,
    UserAccessComponent,
    PlacementsComponent,
    PlacementEditComponent,
    PlacementFloorsComponent,
    DomainBundlesComponent,
    FeatureToggleDirective,
    ExcludeSelectOptionPipe,
    EllipsisAndTooltipDirective,
    FormatTextPipe,
    OnScrollToElementDirective,
    PublisherSellersComponent,
    SanitizerPipe,
    GtmIdImplementDirective,
    NotFoundComponent,
    ForbiddenComponent,
    FixedTableHeaderDirective,
    AppModalComponent,
    PasswordValidatorComponent,
    UserFormPasswordComponent,
    AuditComponent,
    UploadContractComponent,
    CustomComponentHostDirective,
    WarningBoxComponent,
    NzDynamicMaxTagCountDirective,
    AppDropdownButtonComponent,
    CommissionFixedCpmWarningTextComponent,
    ContentControlsComponent,
    CustomizeColumnsComponent,
    GeoTargetingComponent,
    AudienceTargetingComponent,
    AudienceTargetingSegmentComponent,
    ShowIfTextLeakedDirective,
    DealExternalSyncComponent,
    MaintenanceComponent,
    UploadFileComponent,
    UploadLogoComponent,
    UploadCsvComponent,
    AddTargetToAlertComponent,
    EditAlertComponent,
    ClusivityListSelectOptionsComponent,
    ClusivityListComponent,
    ClusivityListSelectOptionGroupsComponent,
    ClusivityListSelectOptionsTreeComponent,
    ClusivityListMultiTextInputComponent,
    CopyToClipboardButtonComponent,
    UserManagementComponent,
    DealPartnerInfoComponent,
    DayPartingComponent,
    DayPartingAccordionComponent,
    ExchangeDealsDropdownComponent,
    PublisherDealsDropdownComponent,
    NumericOnlyDirective,
    PublisherDealsDropdownComponent,
    PhoneInputComponent,
    UniversalMediaDomainsComponent,
    AppLoadingComponent,
    UniversalMediaListContainerComponent,
    PadAgreementDropdownComponent,
    ExchangeAuditComponent,
    ExchangeAuditDetailsComponent
  ],
  declarations: [
    TruncatePipe,
    MapFromKeyValuePipe,
    WidgetFormatPipe,
    SimplePercentPipe,
    StripHtmlPipe,
    AppTableComponent,
    PublisherStatusPillComponent,
    ClickOutsideDirective,
    LogPipe,
    // Assets
    AppIcon,
    SvgUnrulyLogo,
    SvgAngular,
    BillingHistoryComponent,
    FocusInvalidDirective,
    SharedTaxInfoComponent,
    SharedPaymentInfoComponent,
    SharedKpiWidgetComponent,
    AppChartComponent,
    // AppBarChartComponent,
    SanitizerPipe,
    IsLoadingPipe,
    CellTagPipe,
    PlacementsRequiredComponent,
    SharedGlobalSearchComponent,
    FormatTextPipe,
    AppFiltersComponent,
    AppInputComponent,
    EnumsPipe,
    SignupInfoComponent,
    UserManagementUpdateComponent,
    UserAccessComponent,
    PlacementsComponent,
    PlacementEditComponent,
    PlacementFloorsComponent,
    PlacementCommissionsComponent,
    BlockedListsComponent,
    DomainBundlesComponent,
    FeatureToggleDirective,
    ExcludeSelectOptionPipe,
    EllipsisAndTooltipDirective,
    OnScrollToElementDirective,
    SortByPipe,
    PublisherSellersComponent,
    GtmIdImplementDirective,
    NotFoundComponent,
    ForbiddenComponent,
    FixedTableHeaderDirective,
    AppModalComponent,
    BlockedAdvertisersComponent,
    PasswordValidatorComponent,
    UserFormPasswordComponent,
    UploadContractComponent,
    AuditComponent,
    BillPaymentInfoComponent,
    CustomComponentHostDirective,
    WarningBoxComponent,
    NzDynamicMaxTagCountDirective,
    AppDropdownButtonComponent,
    CommissionFixedCpmWarningTextComponent,
    ContentControlsComponent,
    CustomizeColumnsComponent,
    GeoTargetingComponent,
    AlertsManagementComponent,
    EditAlertComponent,
    AudienceTargetingComponent,
    AudienceTargetingSegmentComponent,
    ShowIfTextLeakedDirective,
    DealExternalSyncComponent,
    MaintenanceComponent,
    UploadFileComponent,
    UploadLogoComponent,
    UploadCsvComponent,
    AddTargetToAlertComponent,
    ClusivityListSelectOptionsComponent,
    ClusivityListComponent,
    ClusivityListSelectOptionGroupsComponent,
    ClusivityListSelectOptionsTreeComponent,
    ClusivityListMultiTextInputComponent,
    CopyToClipboardButtonComponent,
    UserManagementComponent,
    DealPartnerInfoComponent,
    DayPartingComponent,
    DayPartingAccordionComponent,
    NumericOnlyDirective,
    ExchangeDealsDropdownComponent,
    PublisherDealsDropdownComponent,
    PhoneInputComponent,
    UniversalMediaDomainsComponent,
    AppLoadingComponent,
    UniversalMediaListContainerComponent,
    PadAgreementDropdownComponent,
    ExchangeAuditComponent,
    ExchangeAuditDetailsComponent
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: VersionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: FilenameInterceptor, multi: true },
    { provide: NZ_ICONS, useValue: SYSTEM_ZORRO_ICONS },
    { provide: NZ_I18N, useValue: en_US },
    { provide: FusionApiHttpClient, useFactory: fusionApiHttpClientFactory, deps: [HttpClient, AngularServiceConfigProvider] },
    { provide: RxDealsHttpClient, useFactory: rxDealsHttpClientFactory, deps: [HttpClient, AngularServiceConfigProvider] },
    { provide: TrafficSourcesApiService, useFactory: trafficSourcesApiServiceFactory, deps: [FusionApiHttpClient] },
    { provide: UniversalMediaListsApiService, useFactory: universalMediaListsApiServiceFactory, deps: [RxDealsHttpClient] },
    { provide: ExchangeBrandsApiService, useFactory: exchangeBrandsApiServiceFactory, deps: [RxDealsHttpClient] },
    { provide: ExchangeDealFormApiService, useFactory: exchangeDealFormApiServiceFactory, deps: [RxDealsHttpClient] },
    { provide: SupplyPadAgreementApiService, useFactory: supplyPadAgreementApiServiceFactory, deps: [FusionApiHttpClient] },
    { provide: PublishersApiService, useFactory: publishersApiServiceFactory, deps: [FusionApiHttpClient] },
    { provide: ExchangePartnersApiService, useFactory: exchangePartnersApiServiceFactory, deps: [FusionApiHttpClient] },
    { provide: PublisherTrafficSourceApiService, useFactory: publisherTrafficSourceApiServiceFactory, deps: [FusionApiHttpClient] },
    { provide: InventoryPackagesApiService, useFactory: inventoryPackagesApiServiceFactory, deps: [FusionApiHttpClient] },
    { provide: DealsApiService, useFactory: dealsApiServiceFactory, deps: [FusionApiHttpClient] },
    Common, FormHelper, PipeManager,
    Title
  ]
})

export class SharedModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(faFileExcel, faRedoAlt, faAngleDown, faCalendarAlt, faAngleUp, faPlus, faSearch, faSearchPlus, faSearchMinus,
      faFilter, faInfo, faTachometerAlt, faArrowLeft, faEdit, faSave, faCopy, faCheck, faQuestionCircle, faUsers, faUser, faSlidersH,
      faExclamationCircle, faTimesCircle, faBriefcase, faUserCircle, faUserMd, faFileInvoiceDollar, faCreditCard, faUpload, faSignOutAlt,
      faDollarSign, faBan, faPlusCircle, faTimes, faCogs, faCog, faBullhorn, faUsersCog, faUnlockAlt, faHistory, faFileAlt,
      faMinusCircle, faShieldAlt, faInfoCircle, faUserCog, faList, faFileUpload, faTrash, faListUl, faUserCheck, faFileContract, faKey,
      faSyncAlt, faCheckCircle, faChevronDown, faChevronUp, faChevronLeft, faChevronRight, faEye, faEyeSlash, faBoxOpen, faSortAmountDown,
      faSortAmountUp, faEquals, faChartArea, faClipboardList, faLink, faArrowCircleLeft, faChartPie, faLayerGroup, faHome, faGripHorizontal,
      faCircle, faArrowAltCircleDown, faArrowAltCircleLeft, faSpinner, faCircleNotch, faTasks, farHdd, farHandshake, farEnvelope,
      farCheckCircle, farTimesCircle, faCaretDown, faCaretUp, faExclamationTriangle, faTable, faChartLine, faPause, faPlay, faBell, faTag,
      faFighterJet, faHandshake, faWrench, faRobot);
  }
}
